import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import DateRangeFilter from "./shared/DateRangeFilter";
import KpiCards from "./shared/KpiCards";
import AnalyticsChart from "./shared/AnalyticsChart";
import AnalyticsDataTable from "./shared/AnalyticsDataTable";
import AnalyticsMetricList from "./shared/AnalyticsMetricList";
import AnalyticsRankedAxisTick from "./shared/AnalyticsRankedAxisTick";
import AnalyticsWorkspaceHero from "./shared/AnalyticsWorkspaceHero";
import useAnalyticsDashboardData from "./shared/useAnalyticsDashboardData";
import {
  ANALYTICS_SERIES_COLORS,
  buildDistributionSummary,
  DistributionTooltip,
  formatDateValue,
  formatNumber,
  formatPercentage,
  formatRangeLabel,
  getKpiValue,
  getPeakEntry,
  getRangeDayCount,
  getTopItem,
  TimeSeriesTooltip,
} from "./shared/analyticsUtils";

function FeedbackSection(props) {
  const { data, loading, currentStartDate, currentEndDate, handleDateChange } = useAnalyticsDashboardData({
    dataUrl: props.dataUrl,
    startDate: props.startDate,
    endDate: props.endDate,
  });
  const locale = props.locale || "en";
  const labels = props.labels;
  const selectedRangeLabel = formatRangeLabel(currentStartDate, currentEndDate, locale);
  const rangeDayCount = getRangeDayCount(currentStartDate, currentEndDate);
  const volumeOverTime = data?.volume_over_time || [];
  const statusBreakdown = data?.status_breakdown || [];
  const byHospital = data?.by_hospital || [];
  const recentUnreplied = data?.recent_unreplied || [];
  const totalComplaints = getKpiValue(data?.kpis, "total");
  const unrepliedCount = getKpiValue(data?.kpis, "unreplied");
  const replyRate = getKpiValue(data?.kpis, "reply_rate");
  const averageDaily = rangeDayCount > 0 ? Math.round(totalComplaints / rangeDayCount) : 0;
  const peakDay = getPeakEntry(volumeOverTime);
  const topStatus = getTopItem(statusBreakdown);
  const topHospital = byHospital[0];
  const loadingLabel = labels.loading || "...";

  const heroItems = [
    {
      label: labels.selectedWindow,
      value: selectedRangeLabel || loadingLabel,
    },
    {
      label: labels.totalInWindow,
      value: loading ? loadingLabel : formatNumber(totalComplaints, locale),
      hint: labels.complaintsUnit,
    },
    {
      label: labels.replyRate,
      value: loading ? loadingLabel : formatPercentage(replyRate, locale),
      hint: labels.replyRateHint,
    },
    {
      label: labels.topHospital,
      value: loading ? loadingLabel : topHospital?.name || labels.noData,
      hint: topHospital ? `${formatNumber(topHospital.value, locale)} ${labels.complaintsUnit}` : null,
    },
  ];

  const filterSummaryItems = [
    {
      label: labels.leadingStatus,
      value: loading ? loadingLabel : topStatus?.label || labels.noData,
      hint: topStatus ? `${formatNumber(topStatus.value, locale)} ${labels.complaintsUnit}` : null,
    },
    {
      label: labels.unrepliedCount,
      value: loading ? loadingLabel : formatNumber(unrepliedCount, locale),
      hint: labels.complaintsUnit,
    },
    {
      label: labels.topHospital,
      value: loading ? loadingLabel : topHospital?.name || labels.noData,
      hint: topHospital ? `${formatNumber(topHospital.value, locale)} ${labels.complaintsUnit}` : null,
    },
  ];

  const trendItems = [
    {
      label: labels.peakDay,
      value: peakDay ? formatNumber(peakDay.count, locale) : labels.noData,
      hint: peakDay ? formatDateValue(peakDay.date, locale, { day: "2-digit", month: "short", year: "numeric" }) : null,
    },
    {
      label: labels.averageDaily,
      value: formatNumber(averageDaily, locale),
      hint: labels.complaintsUnit,
    },
    {
      label: labels.replyRate,
      value: formatPercentage(replyRate, locale),
      hint: labels.replyRateHint,
    },
  ];

  const statusSummary = buildDistributionSummary(statusBreakdown, locale, labels.complaintsUnit);
  const hospitalSummary = buildDistributionSummary(byHospital, locale, labels.complaintsUnit, { labelKey: "name" });

  const columns = [
    { key: "name", label: labels.name },
    { key: "hospital_name", label: labels.hospital },
    {
      key: "message",
      label: labels.message,
      tdClassName: "analytics-data-table__cell--wide",
      render: (row) => <span className="analytics-data-table__copy">{row.message}</span>,
    },
    { key: "visit_date", label: labels.visitDate },
    { key: "created_at", label: labels.submittedAt },
  ];

  return (
    <div className="analytics-workspace">
      <AnalyticsWorkspaceHero
        eyebrow={labels.heroEyebrow}
        title={labels.heroTitle}
        description={labels.heroDescription}
        items={heroItems}
      />

      <DateRangeFilter
        startDate={currentStartDate}
        endDate={currentEndDate}
        labels={labels}
        onChange={handleDateChange}
        title={labels.filterTitle}
        description={labels.filterDescription}
        rangeLabel={selectedRangeLabel}
        summaryItems={filterSummaryItems}
      />

      <KpiCards kpis={data?.kpis} loading={loading} periodLabel={labels.vsPreviousPeriod} locale={locale} />

      <div className="row">
        <div className="col-xl-7 mb-4">
          <AnalyticsChart
            title={labels.volumeOverTime}
            description={labels.volumeOverTimeDescription}
            loading={loading}
            isEmpty={!volumeOverTime.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-chart"
            aside={<AnalyticsMetricList items={trendItems} emptyLabel={labels.noData} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={volumeOverTime} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.28)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatDateValue(value, locale, { day: "2-digit", month: "short" })}
                  minTickGap={24}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<TimeSeriesTooltip locale={locale} unitLabel={labels.complaintsUnit} />} />
                <Line type="monotone" dataKey="count" stroke="#F97316" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>

        <div className="col-xl-5 mb-4">
          <AnalyticsChart
            title={labels.statusBreakdown}
            description={labels.statusBreakdownDescription}
            loading={loading}
            isEmpty={!statusBreakdown.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-comment-alert"
            footer={<AnalyticsMetricList items={statusSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={statusBreakdown}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={108}
                  paddingAngle={3}
                  stroke="none"
                >
                  {statusBreakdown.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.complaintsUnit} />} />
              </PieChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            title={labels.byHospital}
            description={labels.byHospitalDescription}
            loading={loading}
            isEmpty={!byHospital.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-hospital"
            footer={<AnalyticsMetricList items={hospitalSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={340}
          >
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={byHospital} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.24)" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" width={180} tick={<AnalyticsRankedAxisTick maxLength={24} />} tickLine={false} axisLine={false} />
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.complaintsUnit} labelKey="name" />} />
                <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                  {byHospital.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsDataTable
            title={labels.recentUnreplied}
            description={labels.recentUnrepliedDescription}
            loading={loading}
            rows={recentUnreplied}
            columns={columns}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-check-circle"
            loadingHeight={240}
          />
        </div>
      </div>
    </div>
  );
}

export default FeedbackSection;
